/**
 * Not currently working when used with the mdx config in gatsby-config.
 */

import Highlight, { defaultProps } from 'prism-react-renderer'
import { Link, Typography } from '@material-ui/core'
import { LiveEditor, LiveError, LivePreview, LiveProvider } from 'react-live'

import { FileCopy } from '@material-ui/icons'
import { IconButton } from '@material-ui/core'
import { MDXProvider } from '@mdx-js/react'
import React from 'react'
import { copyToClipboard } from '../../utils/helpers'
import hightlightTheme from 'prism-react-renderer/themes/nightOwl'
import { makeStyles } from '@material-ui/core/styles'
import { withStyles } from '@material-ui/styles'

let highlightStart = false

const highlightLine = (lineArray, lineProps, className) => {
  let shouldExclude = false

  lineArray.forEach((line) => {
    const content = line.content

    // Highlight lines with "// highlight-line"
    if (content.replace(/\s/g, '').includes('//highlight-line')) {
      lineProps.className = `${lineProps.className} ${className}`
      line.content = content
        .replace('// highlight-line', '')
        .replace('//highlight-line', '')
    }

    // Stop highlighting
    if (highlightStart && content.replace(/\s/g, '') === '//highlight-end') {
      highlightStart = false
      shouldExclude = true
    }

    // Start highlighting after "//highlight-start"
    if (content.replace(/\s/g, '') === '//highlight-start') {
      highlightStart = true
      shouldExclude = true
    }
  })

  // Highlight lines between //highlight-start & //highlight-end
  if (highlightStart) {
    lineProps.className = `${lineProps.className} ${className}`
  }

  return shouldExclude
}

const useCodeStyles = makeStyles({
  root: {
    padding: 0,
    margin: 0,
    backgroundColor: 'rgb(1, 22, 39)',
    border: `1px solid black`,
    borderRadius: 5,
  },
  codeHighlight: {
    background: '#334c61',
    borderLeft: '0.25rem solid rgb(199, 146, 234)',
    paddingLeft: 5,
    width: '100%',
  },
  tokenLine: {
    display: 'flex',
  },
})

const textStyles = {
  fontSize: 21,
  fontFamily: 'PT Serif, serif',
  color: 'rgba(0, 0, 0, 0.80)',
  fontWeight: 400,
  wordBreak: 'break-word',
}

const CodeElement = withStyles({
  root: {
    background: '#e8f4fd',
    padding: `0 5px`,
    wordBreak: 'break-word',
    borderBottom: `1px solid rgba(0,0,0,0.2)`,
  },
})((props) => <code className={props.classes.root}>{props.children}</code>)

const CustomBlogP = withStyles({
  root: {
    ...textStyles,
  },
})(Typography)

const CustomLI = withStyles({
  root: {
    ...textStyles,
    margin: '8px 0',
  },
})((props) => <li className={props.classes.root}>{props.children}</li>)

const Pre = withStyles((theme) => ({
  root: {
    fontSize: 16,
    padding: theme.spacing(1),
    overflow: 'auto',
    textAlign: 'left',
    fontFamily: 'Courier New, Courier, monospace',
    position: 'relative',
    [theme.breakpoints.down('sm')]: {
      width: 300,
    },
  },
}))((props) => <pre className={props.classes.root}>{props.children}</pre>)

const LineNumber = withStyles({
  root: {
    display: 'inline-block',
    paddingRight: '.2em',
    userSelect: 'none',
    color: '#6692b7',
  },
})((props) => <span className={props.classes.root}>{props.children}</span>)

const CopyCodeIcon = withStyles({
  root: {
    margin: '0.25em',
    float: 'right',
    zIndex: 3000,
    opacity: 0.6,
    '&:hover': {
      opacity: 1,
    },
  },
})((props) => {
  return (
    <IconButton {...props} color="secondary">
      <FileCopy />
    </IconButton>
  )
})

const Code = ({ codeString, language, ...props }) => {
  const classes = useCodeStyles()

  if (props['react-live']) {
    return (
      <LiveProvider code={codeString} noInline={true} theme={hightlightTheme}>
        <LiveEditor />
        <LiveError />
        <LivePreview />
      </LiveProvider>
    )
  }

  let currentLineNumber = 0

  return (
    <div className={classes.root}>
      <CopyCodeIcon onClick={() => copyToClipboard(codeString)} />
      <Highlight
        {...defaultProps}
        code={codeString}
        language={language}
        theme={hightlightTheme}
      >
        {({ className, style, tokens, getLineProps, getTokenProps }) => (
          <Pre className={className} style={style}>
            {tokens.map((line, i) => {
              const lineProps = getLineProps({ line, key: i })
              const shouldExclude = highlightLine(
                line,
                lineProps,
                classes.codeHighlight
              )
              return !shouldExclude ? (
                <div key={i} className={classes.tokenLine}>
                  <LineNumber>{`${(currentLineNumber += 1)} `}</LineNumber>
                  <div {...lineProps}>
                    {line.map((token, key) => (
                      <span key={key} {...getTokenProps({ token, key })} />
                    ))}
                  </div>
                </div>
              ) : null
            })}
          </Pre>
        )}
      </Highlight>
    </div>
  )
}

const H1ANDH2 = ({ Component }) =>
  withStyles({
    anchorLink: {
      position: 'absolute',
      marginTop: -50,
    },
    anchorLinkStyle: {
      display: 'none',
    },
  })((props) => {
    return (
      <Component>
        {/* eslint-disable jsx-a11y/anchor-has-content */}
        <a
          className={props.classes.anchorLink}
          aria-label="anchor"
          id={props.id}
          href="/"
        />
        {props.children}
        <a
          className={props.classes.anchorLinkStyle}
          aria-hidden
          aria-label="anchor"
          href={`#${props.id}`}
        />
      </Component>
    )
  })

export const BLOG_COMPONENT_OVERRIDES = {
  p: CustomBlogP,
  li: CustomLI,
  a: (props) => <Link {...props} underline="none" />,
  h1: H1ANDH2({ Component: 'h1' }),
  h2: H1ANDH2({ Component: 'h2' }),
  pre: ({ children: { props } }) => {
    if (props.mdxType === 'code') {
      return (
        <Code
          codeString={props.children.trim()}
          language={props.className && props.className.replace('language-', '')}
          {...props}
        />
      )
    }
  },
  'p.inlineCode': CodeElement,
}

export default function MDXLayout({ children, shortCodes }) {
  return (
    <MDXProvider
      components={{ p: (props) => <Typography {...props} />, ...shortCodes }}
    >
      {children}
    </MDXProvider>
  )
}
